import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { getRuntimeConfigEntry, setRuntimeConfig } from "@/lib/api";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./game-config.css";

const GAME_VERSIONS_KEY = "allowed_game_versions";

function parseGameVersions(value: string | undefined): string[] {
  if (!value?.trim()) return [];
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || !Array.isArray((parsed as { versions?: unknown }).versions) || (parsed as { versions: unknown[] }).versions.some((version) => typeof version !== "string")) {
    throw new Error('Stored game versions must have a string list in "versions".');
  }
  return (parsed as { versions: string[] }).versions;
}

export function GameVersionsWorkspace() {
  const readOnly = usePreviewReadOnly();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["runtime-config-entry", GAME_VERSIONS_KEY],
    queryFn: async () => parseGameVersions((await getRuntimeConfigEntry(GAME_VERSIONS_KEY))?.value),
  });
  const [versions, setVersions] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized && query.isSuccess) { setVersions(query.data ?? []); setInitialized(true); }
  }, [initialized, query.isSuccess, query.data]);

  const save = useMutation({
    mutationFn: () => {
      const next = versions.map((version) => version.trim());
      if (next.some((version) => !version)) throw new Error("Every game version must have a value.");
      if (new Set(next).size !== next.length) throw new Error("Each game version may appear only once.");
      return setRuntimeConfig(GAME_VERSIONS_KEY, JSON.stringify({ versions: next }));
    },
    onSuccess: () => {
      setVersions((current) => current.map((version) => version.trim()));
      queryClient.invalidateQueries({ queryKey: ["runtime-config-entry", GAME_VERSIONS_KEY] });
    },
  });

  const storedVersions = query.data ?? [];
  const dirty = JSON.stringify(versions) !== JSON.stringify(storedVersions);

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "Game versions" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page is-narrow">
      <div className="ws-head"><h1>Game versions</h1>
        {query.isSuccess && <div className="ws-head-actions">
          {save.isSuccess && !dirty && <span className="gc-status" role="status">Saved</span>}
          {dirty && <button className="pw-button pw-button-primary" onClick={() => save.mutate()} disabled={save.isPending || readOnly}>{save.isPending ? "Saving…" : "Save"}</button>}
        </div>}
      </div>
      {save.error && <p className="ws-alert" role="alert">{String(save.error)}</p>}
      {query.isLoading ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading versions…</div> : query.isError ? (
        <div className="pw-error" role="alert"><strong>Couldn’t load game versions</strong><p>{String(query.error)}</p><button className="pw-button" onClick={() => void query.refetch()}>Try again</button></div>
      ) : <section className="ws-card ws-form" aria-label="Allowed versions">
        {versions.length === 0 ? <p className="gc-none">No versions allowed</p> : <ul className="gc-versions">{versions.map((version, index) => <li key={index}>
          <input className="gc-mono" aria-label={`Game version ${index + 1}`} value={version} placeholder="e.g. 2.2.0"
            onChange={(event) => setVersions((current) => current.map((value, i) => i === index ? event.target.value : value))} />
          <button type="button" className="pw-icon-button gc-remove" aria-label={`Remove version ${index + 1}`} title="Remove version" onClick={() => setVersions((current) => current.filter((_, i) => i !== index))}><X size={15} /></button>
        </li>)}</ul>}
        <button type="button" className="pw-button gc-add" onClick={() => setVersions((current) => [...current, ""])}><Plus size={14} />Add version</button>
      </section>}
    </div></div>
  </WorkspaceShell>;
}
